import { OFFERED_BEHAVIORS } from '../../stores/utils/column-property-spec';
// TYPE-ONLY: the runtime store drags the Lit design system into node-env
// unit tests.
import type { TableRow } from '../../stores/table-store';
import {
  normVar,
  splitList,
} from '../table/node-property-panel/ai-action-assist/action-assist-model';

/**
 * Project Bobcat — the solution assistant's node proposals
 * (`bobcat-solution-assistant`). The model proposes content for any node;
 * this checks every proposed value against the REAL solution before the
 * designer sees it, and turns the fields they keep into the row update Apply
 * writes. Pure, so it tests in node.
 */

export type ProposalColumn =
  | 'nodeName'
  | 'message'
  | 'utterances'
  | 'nextNodes'
  | 'behaviors'
  | 'answerRequired'
  | 'description'
  | 'command'
  | 'nodeInput'
  | 'parameterInput'
  | 'output'
  | 'decisionVariable'
  | 'whatsNext'
  | 'variables';

export interface ProposalField {
  column: ProposalColumn;
  value: string;
  why: string;
}

/** What the chat route returns for one node. */
export interface NodeProposalDraft {
  /** An existing node's number; empty for a node to be created. */
  nodeNumber: string;
  fields: ProposalField[];
  /** A node that does not exist yet (the chat route's `NodeProposalCreate`). */
  create?: { agent: string; nodeType: 'D' | 'A' };
}

/**
 * Where a proposed NEW node goes: an existing agent (its group row id), or a
 * new agent of that name — created on Apply, not before.
 */
export type ProposalCreateTarget =
  | { agent: string; agentId: string }
  | { agent: string; agentId: null };

export type ProposalNodeKind = 'decision' | 'action';

/** A proposal that passed validation: every field fits this solution. */
export interface NodeProposal {
  /**
   * The node's row id; for a node to be created, a placeholder
   * (`new:<n>`) that keys the review card until Apply creates the row.
   */
  rowId: string;
  /** Set when the node is new: the agent it goes into. */
  create?: ProposalCreateTarget;
  nodeNumber: string;
  nodeName: string;
  kind: ProposalNodeKind;
  fields: ProposalField[];
  /** Anything dropped or trimmed, said plainly for the review. */
  notes: string[];
}

export const DECISION_COLUMNS: readonly ProposalColumn[] = [
  'nodeName',
  'message',
  'utterances',
  'nextNodes',
  'behaviors',
  'answerRequired',
  'description',
];

export const ACTION_COLUMNS: readonly ProposalColumn[] = [
  'nodeName',
  'command',
  'description',
  'nodeInput',
  'parameterInput',
  'output',
  'decisionVariable',
  'whatsNext',
  'variables',
];

/** Review-card labels. */
export const PROPOSAL_COLUMN_LABELS: Record<ProposalColumn, string> = {
  nodeName: 'Node Name',
  message: 'Message',
  utterances: 'Example Phrases',
  nextNodes: 'Next Nodes',
  behaviors: 'Behaviors',
  answerRequired: 'Answer Required',
  description: 'Description',
  command: 'Command',
  nodeInput: 'Node Input',
  parameterInput: 'Parameter Input',
  output: 'Output',
  decisionVariable: 'Decision Variable',
  whatsNext: 'What Next?',
  variables: 'Variable',
};

/** The fields of a row this module reads — a structural slice of TableRow. */
export interface ProposalRow {
  id: string;
  nodeNumber?: number | string | null;
  nodeName?: unknown;
  nodeType?: unknown;
  /** A group row's name lives here — how a new node finds its agent. */
  settings?: { groupName?: unknown } | null;
}

/** The existing agent (group row) with this name, matched case-blind. */
export const findAgentByName = (
  rows: ProposalRow[],
  name: string
): ProposalRow | undefined => {
  const want = name.trim().toLowerCase();
  return rows.find(
    (r) =>
      String(r.nodeType ?? '').toLowerCase() === 'g' &&
      String(r.settings?.groupName ?? '')
        .trim()
        .toLowerCase() === want
  );
};

/** Agents a proposal may never create a second one of. */
const CORE_AGENT_NAMES = ['supervisor agent', 'knowledge agent'];

const kindOf = (row: ProposalRow): ProposalNodeKind | null => {
  const t = String(row.nodeType ?? '').toLowerCase();
  if (t === 'd') return 'decision';
  if (t === 'a') return 'action';
  return null;
};

const columnsFor = (kind: ProposalNodeKind) =>
  kind === 'decision' ? DECISION_COLUMNS : ACTION_COLUMNS;

const nodeNumbersOf = (rows: ProposalRow[]): Set<string> =>
  new Set(
    rows
      .filter((r) => kindOf(r) && r.nodeNumber != null)
      .map((r) => String(r.nodeNumber))
  );

/**
 * One field checked against the solution. Returns the value to keep (possibly
 * trimmed of bad parts) and a note when something was dropped, or `null` when
 * nothing usable is left.
 */
const checkField = (
  field: ProposalField,
  known: Set<string>,
  commands: Set<string>
): { value: string | null; note?: string } => {
  const label = PROPOSAL_COLUMN_LABELS[field.column];
  switch (field.column) {
    case 'command': {
      const cmd = field.value.trim();
      if (commands.has(cmd)) return { value: cmd };
      return {
        value: null,
        note: `Command ${cmd} isn't uploaded in this version — upload its script in Actions first, then set it.`,
      };
    }
    case 'nextNodes': {
      const nums = splitList(field.value).map((n) => n.replace(/^#/, ''));
      const good = nums.filter((n) => known.has(n));
      const bad = nums.filter((n) => !known.has(n));
      return {
        value: good.length ? good.join(',') : null,
        ...(bad.length
          ? {
              note: `${label}: dropped ${bad.join(', ')} — not in this solution.`,
            }
          : {}),
      };
    }
    case 'whatsNext': {
      const pairs = field.value
        .split('|')
        .map((p) => p.trim())
        .filter(Boolean);
      const good: string[] = [];
      const bad: string[] = [];
      pairs.forEach((pair) => {
        const [outcome, dest] = pair.split('~').map((s) => s?.trim() ?? '');
        const num = dest?.replace(/^#/, '');
        if (outcome && num && known.has(num)) good.push(`${outcome}~${num}`);
        else bad.push(pair);
      });
      return {
        value: good.length ? good.join('|') : null,
        ...(bad.length
          ? {
              note: `${label}: dropped ${bad.join(', ')} — each needs outcome~node, with a node in this solution.`,
            }
          : {}),
      };
    }
    case 'behaviors': {
      const items = splitList(field.value).map((b) => b.trim().toLowerCase());
      const good = items.filter((b) => OFFERED_BEHAVIORS.includes(b));
      const bad = items.filter((b) => !OFFERED_BEHAVIORS.includes(b));
      return {
        value: good.length ? good.join(',') : null,
        ...(bad.length
          ? { note: `${label}: dropped ${bad.join(', ')} — not a behavior.` }
          : {}),
      };
    }
    case 'answerRequired': {
      const v = field.value.trim().toLowerCase();
      if (v === 'true' || v === 'false') return { value: v };
      return {
        value: null,
        note: `${label}: "${field.value}" isn't true/false.`,
      };
    }
    case 'parameterInput': {
      try {
        const parsed: unknown = JSON.parse(field.value);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          return { value: JSON.stringify(parsed, null, 2) };
        }
      } catch {
        // falls through to the note
      }
      return {
        value: null,
        note: `${label} wasn't a JSON object, so it was left out.`,
      };
    }
    case 'variables':
    case 'nodeInput': {
      const v = splitList(field.value).map(normVar).filter(Boolean).join(',');
      return { value: v || null };
    }
    case 'output': {
      const v = splitList(field.value).map(normVar).filter(Boolean).join(',');
      return { value: v || null };
    }
    case 'decisionVariable': {
      const v = normVar(field.value);
      return { value: v || null };
    }
    case 'utterances': {
      const v = field.value
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter(Boolean)
        .join('\n');
      return { value: v || null };
    }
    default:
      return { value: field.value.trim() || null };
  }
};

/**
 * Keeps only what fits the solution: nodes that exist (group rows excluded),
 * columns that belong to the node's type, commands the version has, and
 * routing to real nodes. A node left with nothing usable is dropped with a
 * note, so the designer hears why rather than seeing nothing.
 */
export const validateNodeProposals = (
  drafts: NodeProposalDraft[],
  rows: ProposalRow[],
  { commands, maxNodes = 6 }: { commands: Iterable<string>; maxNodes?: number }
): { proposals: NodeProposal[]; notes: string[] } => {
  const known = nodeNumbersOf(rows);
  const commandSet = new Set(commands);
  const byNumber = new Map<string, ProposalRow>();
  rows.forEach((r) => {
    if (
      kindOf(r) &&
      r.nodeNumber != null &&
      !byNumber.has(String(r.nodeNumber))
    ) {
      byNumber.set(String(r.nodeNumber), r);
    }
  });

  const proposals: NodeProposal[] = [];
  const notes: string[] = [];
  const seen = new Set<string>();

  let newCount = 0;
  drafts.forEach((draft) => {
    const { create } = draft;
    const num = create
      ? ''
      : String(draft.nodeNumber ?? '')
          .replace(/^#/, '')
          .trim();
    if (!create) {
      if (seen.has(num)) return;
      seen.add(num);
    }
    if (proposals.length >= maxNodes) {
      notes.push(
        create
          ? `Left out a new node for ${create.agent}: at most ${maxNodes} nodes per request.`
          : `Left out #${num}: at most ${maxNodes} nodes per request.`
      );
      return;
    }
    let row: ProposalRow | undefined;
    let kind: ProposalNodeKind | null;
    let target: ProposalCreateTarget | undefined;
    if (create) {
      kind = create.nodeType === 'A' ? 'action' : 'decision';
      const existing = findAgentByName(rows, create.agent);
      if (
        !existing &&
        CORE_AGENT_NAMES.includes(create.agent.trim().toLowerCase())
      ) {
        notes.push(
          `Didn't propose a second ${create.agent}: a solution has one.`
        );
        return;
      }
      target = existing
        ? { agent: create.agent, agentId: existing.id }
        : { agent: create.agent, agentId: null };
    } else {
      row = byNumber.get(num);
      kind = row ? kindOf(row) : null;
      if (!row || !kind) {
        notes.push(
          `Node ${num} isn't in this solution, so nothing was proposed for it.`
        );
        return;
      }
    }
    const allowed = new Set(columnsFor(kind));
    const nodeNotes: string[] = [];
    const fields: ProposalField[] = [];
    const cols = new Set<string>();
    draft.fields.forEach((f) => {
      if (cols.has(f.column)) return;
      if (!allowed.has(f.column)) {
        nodeNotes.push(
          `${PROPOSAL_COLUMN_LABELS[f.column] ?? f.column} doesn't apply to a ${kind} node.`
        );
        return;
      }
      const { value, note } = checkField(f, known, commandSet);
      if (note) nodeNotes.push(note);
      if (value === null) return;
      cols.add(f.column);
      fields.push({ ...f, value });
    });
    // A new node is worth creating even before it has content; an existing
    // one with nothing usable proposed is not worth a card.
    if (!fields.length && !target) {
      notes.push(`Nothing usable was proposed for ${num}.`, ...nodeNotes);
      return;
    }
    const order = columnsFor(kind);
    fields.sort((a, b) => order.indexOf(a.column) - order.indexOf(b.column));
    if (target) {
      newCount += 1;
      proposals.push({
        rowId: `new:${newCount}`,
        create: target,
        nodeNumber: 'New',
        nodeName:
          fields.find((f) => f.column === 'nodeName')?.value ??
          `${target.agent} node`,
        kind,
        fields,
        notes: nodeNotes,
      });
      return;
    }
    proposals.push({
      rowId: (row as ProposalRow).id,
      nodeNumber: num,
      nodeName:
        typeof row?.nodeName === 'string'
          ? row.nodeName
          : String(row?.nodeName ?? ''),
      kind,
      fields,
      notes: nodeNotes,
    });
  });

  return { proposals, notes };
};

/** The current value of a column as panel text — for "new" vs "replaces". */
export const currentProposalText = (
  row: Record<string, unknown>,
  column: ProposalColumn
): string => {
  const v = row[column];
  if (v == null) return '';
  if (Array.isArray(v)) {
    return v
      .map(String)
      .filter(Boolean)
      .join(column === 'whatsNext' ? '|' : ',');
  }
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  if (typeof v === 'object') {
    const d = (v as { default?: unknown }).default;
    if (typeof d === 'string') return d;
    try {
      return JSON.stringify(v);
    } catch {
      return '';
    }
  }
  return String(v);
};

/** The value committed for each kept field, merged into one row update. The
 *  column conversion is the node panel's own (`buildPanelCellCommitUpdate`),
 *  injected so this stays free of the store. */
export const buildProposalUpdates = (
  proposal: NodeProposal,
  keep: Record<string, boolean>,
  commitUpdate: (column: ProposalColumn, text: string) => Partial<TableRow>
): Partial<TableRow> => {
  const updates: Partial<TableRow> = {};
  proposal.fields
    .filter((f) => keep[f.column] !== false)
    .forEach((f) => Object.assign(updates, commitUpdate(f.column, f.value)));
  return updates;
};

/** Every field kept by default, as the Assisted build review starts. */
export const defaultKeep = (proposal: NodeProposal): Record<string, boolean> =>
  Object.fromEntries(proposal.fields.map((f) => [f.column, true]));
